class WinespotHeader extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    this.bar = this.querySelector('.ws-header__bar');
    this.expansion = this.querySelector('[data-header-expansion]');
    this.panels = [...this.querySelectorAll('[data-header-panel]')];
    this.triggers = [...this.querySelectorAll('[data-header-toggle]')];
    this.hoverQuery = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    this.mobileQuery = matchMedia('(max-width: 749px)');
    this.lastScroll = Math.max(0, window.scrollY);
    this.mode = null;
    const options = { signal: this.events.signal };
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (event.detail > 0 && this.openedFromHover && this.mode === trigger.dataset.headerToggle) {
          this.openedFromHover = false;
          return;
        }
        this.mode === trigger.dataset.headerToggle ? this.close() : this.open(trigger.dataset.headerToggle);
      }, options);
      if (trigger.dataset.headerToggle === 'menu') {
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && this.hoverQuery.matches && this.mode !== 'menu') this.open('menu', true);
        }, options);
      }
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && this.hoverQuery.matches && this.mode === 'menu') this.close(false);
    }, options);
    this.addEventListener('keydown', (event) => this.onKeyDown(event), options);
    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!this.mode || this.contains(document.activeElement)) return;
        if (this.mode === 'menu' && this.hoverQuery.matches && this.matches(':hover')) return;
        this.close(false);
      });
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('#cart-icon-bubble')) this.close(false);
    }, { ...options, capture: true });
    document.addEventListener('pointerdown', (event) => {
      if (this.mode && !this.contains(event.target)) this.close(false);
    }, options);
    window.addEventListener('scroll', () => this.updateScroll(), { ...options, passive: true });
    window.addEventListener('resize', () => this.measure(), options);
    window.addEventListener('pageshow', () => this.updateScroll(), options);
    this.mobileQuery.addEventListener('change', () => {
      this.updateScrollLock();
      this.measure();
    }, options);
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.bar);
    this.panels.forEach((panel) => this.observer.observe(panel));
    this.measure();
    this.updateScroll();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.observer?.disconnect();
    document.body.classList.remove('ws-header-scroll-locked');
  }

  open(mode, fromHover = false) {
    const panel = this.panels.find((item) => item.dataset.headerPanel === mode);
    const trigger = this.triggers.find((item) => item.dataset.headerToggle === mode);
    if (!panel || !trigger) return;
    const focusWasInPanel = this.panels.some((item) => item.contains(document.activeElement));
    this.querySelector('predictive-search')?.close();
    this.mode = mode;
    this.openedFromHover = fromHover;
    this.returnFocus = trigger;
    this.dataset.panel = mode;
    this.dataset.retracted = 'false';
    this.panels.forEach((item) => { item.hidden = item !== panel; });
    this.triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
    this.updateTriggerLabels();
    this.expansion.inert = false;
    this.updateSurface();
    this.updateScrollLock();
    this.measure();
    if (mode === 'search') panel.querySelector('input[type="search"]')?.focus({ preventScroll: true });
    else if (!fromHover || focusWasInPanel) trigger.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    if (!this.mode) return;
    this.querySelector('predictive-search')?.close();
    this.mode = null;
    this.openedFromHover = false;
    this.dataset.panel = 'closed';
    this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    this.updateTriggerLabels();
    this.expansion.inert = true;
    this.panels.forEach((item) => { item.hidden = true; });
    this.style.setProperty('--panel-height', '0px');
    document.body.classList.remove('ws-header-scroll-locked');
    if (restoreFocus) this.returnFocus?.focus({ preventScroll: true });
    this.lastScroll = Math.max(0, window.scrollY);
    this.updateSurface();
  }

  onKeyDown(event) {
    if (!this.mode) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || (this.mode !== 'search' && !this.mobileQuery.matches)) return;
    const elements = [...this.querySelectorAll('a[href], button, input:not([type="hidden"]), select, [tabindex="0"]')]
      .filter((item) => !item.disabled && !item.closest('[hidden], [inert]') && item.getClientRects().length);
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  updateTriggerLabels() {
    this.triggers.forEach((trigger) => {
      if (trigger.dataset.label) trigger.setAttribute('aria-label', this.mode === trigger.dataset.headerToggle ? trigger.dataset.closeLabel : trigger.dataset.label);
    });
  }

  measure() {
    const announcement = document.querySelector('.announcement-bar-section');
    const offset = Math.max(0, (announcement?.offsetHeight || 0) - window.scrollY);
    const barHeight = this.bar.getBoundingClientRect().height;
    this.style.setProperty('--header-offset', `${offset}px`);
    this.style.setProperty('--bar-height', `${barHeight}px`);
    document.documentElement.style.setProperty('--header-height', `${barHeight}px`);
    const panel = this.panels.find((item) => !item.hidden);
    this.style.setProperty('--panel-height', panel ? `${panel.getBoundingClientRect().height}px` : '0px');
  }

  updateScrollLock() {
    document.body.classList.toggle('ws-header-scroll-locked', Boolean(this.mode && this.mobileQuery.matches));
  }

  updateSurface() {
    const cover = document.querySelector('.homepage-cover');
    const overCover = this.dataset.home === 'true' && cover && cover.getBoundingClientRect().bottom > this.bar.offsetHeight;
    this.dataset.surface = !this.mode && (this.dataset.retracted === 'true' || (overCover && window.scrollY <= 0)) ? 'transparent' : 'solid';
    this.dataset.cover = String(Boolean(overCover && this.dataset.surface === 'transparent'));
  }

  updateScroll() {
    const scroll = Math.max(0, window.scrollY);
    const focused = this.contains(document.activeElement);
    if (this.mode || focused || scroll <= 0) this.dataset.retracted = 'false';
    else if (scroll > this.lastScroll + 2) this.dataset.retracted = 'true';
    else if (scroll < this.lastScroll - 2) this.dataset.retracted = 'false';
    this.lastScroll = scroll;
    this.measure();
    this.updateSurface();
  }
}

customElements.define('ws-header', WinespotHeader);
